import { useEffect, useId, useState, type ReactNode } from "react";
import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown, Circle, CircleCheck } from "lucide-react";
import type { Locale } from "@nh/shared";
import { FormGrid } from "@/features/admin/components";
import { cn } from "@/shared/lib/cn";

/**
 * Lo único que recibe un bloque editorial: qué idioma se está
 * escribiendo. Todo lo demás lo saca del formulario que lo envuelve.
 */
export interface EditorialBlockLocaleProps {
  locale: Locale;
}

export interface EditorialBlockProps {
  /** La raíz del bloque en el formulario: `"editorial.es.pillars"`. */
  name: string;
  title: string;
  description: string;
  /** `true` cuando el bloque ya tiene algo escrito. */
  filled: boolean;
  children: ReactNode;
}

/**
 * Uno de los once bloques de la ficha larga, plegable.
 *
 * ── Por qué se pliegan ──────────────────────────────────────────────
 * Once bloques abiertos son más de sesenta campos en una sola pantalla:
 * quien va a escribir solo el cierre tendría que pasar por delante de
 * todo lo demás para encontrarlo. Plegados, el paso 10 cabe de un
 * vistazo y se abre lo que se va a escribir.
 *
 * ── Plegado ocupa una celda; abierto, la fila entera ────────────────
 * Los once viven en una rejilla de tres columnas (ver `EditorialStep`).
 * Plegado, un bloque es un título y una línea: cabe en un tercio y así
 * el índice de los once se ve de una vez. Abierto son títulos,
 * entradillas y listas que crecen, y a 300 px eso no se escribe — así
 * que al abrirse se lleva la fila.
 *
 * Lo hace con `col-span-full` y no moviéndose de sitio: el bloque sigue
 * donde estaba en el documento, de modo que el tabulador y el lector de
 * pantalla lo encuentran en el mismo orden abierto que cerrado.
 *
 * ── Y por qué desplegar no borra nada ───────────────────────────────
 * Al plegar, los campos se DESMONTAN. react-hook-form conserva por
 * omisión (`shouldUnregister: false`) los valores de los campos
 * desmontados y los repone al montar, así que un bloque escrito y
 * plegado viaja igual en el envío. Es la misma propiedad de la que ya
 * dependen las pestañas de idioma.
 *
 * ── Un bloque con un error se abre solo ─────────────────────────────
 * Si el error de un bloque plegado se quedara dentro, el botón de
 * guardar no haría nada y no habría en pantalla nada que explicara por
 * qué. Es el mismo motivo por el que un error del servidor devuelve al
 * paso donde vive el campo.
 *
 * El estado —vacío, escrito, con problema— va en el encabezado, con
 * icono Y con texto: quien no distingue el verde del ámbar lo lee.
 */
export function EditorialBlock({
  name,
  title,
  description,
  filled,
  children,
}: EditorialBlockProps) {
  const { t } = useTranslation();
  const { formState } = useFormContext();
  const panelId = useId();

  const hasError = Boolean(get(formState.errors, name));
  const [open, setOpen] = useState(filled);

  useEffect(() => {
    if (hasError) setOpen(true);
  }, [hasError]);

  const StateIcon = hasError ? AlertTriangle : filled ? CircleCheck : Circle;
  const stateText = hasError
    ? t("Con un problema")
    : filled
      ? t("Escrito")
      : t("Vacío");

  return (
    <fieldset
      className={cn(
        "rounded-2xl border bg-ds-surface",
        open && "col-span-full",
        hasError ? "border-ds-terracotta" : "border-ds-border",
      )}
    >
      <legend className="sr-only">{title}</legend>

      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-start gap-3 rounded-2xl px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50"
      >
        <StateIcon
          aria-hidden="true"
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0",
            hasError
              ? "text-ds-terracotta"
              : filled
                ? "text-ds-success"
                : "text-ds-ink-3",
          )}
        />

        <span className="flex-1">
          <span className="block text-[15px] font-semibold text-ds-ink">{title}</span>
          <span className="mt-0.5 block text-[13px] leading-relaxed text-ds-ink-3">
            {description}
          </span>
          <span className="sr-only">{` — ${stateText}`}</span>
        </span>

        <ChevronDown
          aria-hidden="true"
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 text-ds-ink-3 transition-transform motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <FormGrid
          id={panelId}
          columns={2}
          className="border-t border-ds-border px-5 py-5"
        >
          {children}
        </FormGrid>
      )}
    </fieldset>
  );
}
